import { ComponentStateTypes } from "components/common/ComponentState";
import Page from "components/common/page/Page";
import { api } from "config/axios-context";
import { useEffect, useState } from "react";
import { useContext } from "react";	
import { getAppContext } from 'config/AppContext';
import { Roles } from "utilities/app-utilities";
import SeasonDropdown from "components/entities/app/season/inputs/SeasonDropdown";
import { SeasonType } from "types/season-type";
import { AgricultureStatsResponse, ClassificationResult } from "types/agriculture/states-type";
import NoData from "components/common/NoData";
import AgricultureStatsSales from "components/entities/agriculture/stats/AgricultureStatsSales";
import AgricultureStatsPropertyView from "components/entities/agriculture/stats/AgricultureStatsPropertyView";
import AgricultureStatsInvestment from "components/entities/agriculture/stats/AgricultureStatsInvestment";
import AgricultureStatsMaintenance from "components/entities/agriculture/stats/AgricultureStatsMaintenance";
import AgricultureStatsHarvests from "components/entities/agriculture/stats/AgricultureStatsHarvests";
import Tabs from "components/common/tabs/Tabs";
import Tab from "components/common/tabs/Tab";
import Translate from "components/common/Translate";
import { AppRoutes, URI_PARAMETER_KEYS } from "@/config/router";
import { useParams } from "react-router-dom";
import { set } from "lodash";

const AgricultureStatsPage = () => {
    const {[URI_PARAMETER_KEYS.TARGET_SEASON_ID]: targetSeasonId} = useParams<{ [URI_PARAMETER_KEYS.TARGET_SEASON_ID]: string }>();
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const context = getAppContext(useContext);
    const [data, setData] = useState<AgricultureStatsResponse>();
    const [season, setSeason] = useState<SeasonType>({
        active: true,
        id: -1,
        name: context.t('common.active')
    });

    const seasonDropDown = <SeasonDropdown
        onChange={v => context.navigate(v ? AppRoutes.AGRICULTURE_SEASON_OVERVIEW.replace(`:${URI_PARAMETER_KEYS.TARGET_SEASON_ID}`, String(v.id)) : AppRoutes.AGRICULTURE_OVERVIEW) }
        label={false}
        value={season}
    />

    useEffect(() => {
        if(!context.userContainsRoles([Roles.AGRICULTURE_SALES_READ, Roles.AGRICULTURE_WORK_READ])) {
            setComponentState('no-permission')
            return;
        }
        loadData().then();
    }, []);

    const loadData = async () => {
        setComponentState('loading');
        try {
            const seasonId = season?.id > 0 ? season.id : (targetSeasonId || '');
            const response = await api.get<AgricultureStatsResponse>(`agriculture/states?seasonId=${seasonId}`);
    
            if(!response.success) {
                throw response.data;
            }

            if(!response.data) {
                setComponentState('nodata');
                return;
            }
            
            setComponentState('');
            setData(response.data);
            setSeason({
                id: response.data.seasonId,
                name: response.data.seasonName,
                active: response.data.isCurrentSeasonActive
            });
        } catch (error) {
            setComponentState('error');
            context.notify('error', 'error.loading-data')
            throw error;
        }
    }

    function getInfoData(infoData: AgricultureStatsResponse) {
        return <div className="pb-10 w-full lg:w-1/2 xxl:w-1/3">
            <div className="flex flex-wrap">
                <AgricultureStatsPropertyView label="agriculture.stats.bruto" value={infoData.brute} propertyType="money" />
                <AgricultureStatsPropertyView label="agriculture.stats.neto" value={infoData.neto} propertyType="money" />
                <AgricultureStatsPropertyView label="agriculture.stats.expenses" value={infoData.totalExpense} propertyType="money" />
            </div>
            <AgricultureStatsSales sales={infoData.sales} forceShow />
            <AgricultureStatsInvestment investment={infoData.investment} />
            <AgricultureStatsMaintenance maintenance={infoData.maintenance} />
            <AgricultureStatsHarvests harvest={infoData.harvest} />
        </div>
    }

    function getClassificationData(fruitId: number, sectionName: string, classifications: ClassificationResult[], forceShow?: boolean) {
        return <div className="pb-10">
            <div className="flex pb-5">
                <h2 className="pr-2"><Translate i18nKey={sectionName} /></h2>
                <div style={{height: '1px'}} className="bg-black dark:bg-white w-full my-auto"></div>
            </div>
            <div className="flex flex-nowrap  overflow-x-auto">
                {classifications.map((classification) => <div key={`agriculture-stats-fruit-tab-${fruitId}_group-${classification.id}`} 
                        className="border-l border-l-sunrise-300 pl-2 min-w-max"
                    >
                    <h3 className="uppercase text-sunrise-300">{classification.name}</h3>
                    <AgricultureStatsSales sales={classification.sales} forceShow={forceShow} showIncome />
                    <AgricultureStatsInvestment investment={classification.investment} />
                    <AgricultureStatsMaintenance maintenance={classification.maintenance} />
                    <AgricultureStatsHarvests harvest={classification.harvest} />
                </div>)}
            </div>
        </div>
    }

    function getFruitTabs() {
        if(!data || !data.fruits?.length) {
            return <NoData />;
        }

        const tabs = data.fruits.map(fruit => 
            <Tab title={fruit.name} name={fruit.name} key={`agriculture-stats-fruit-tab-${fruit.id}`}>
                <div className="pb-10 w-full lg:w-1/2 xxl:w-1/3">
                    <AgricultureStatsSales sales={fruit.sales} showIncome />
                    <AgricultureStatsInvestment investment={fruit.investment} />
                    <AgricultureStatsMaintenance maintenance={fruit.maintenance} />
                    <AgricultureStatsHarvests harvest={fruit.harvest} />
                </div>
                {getClassificationData(fruit.id, context.t('fields.category.name'), fruit.category, true)}
                {getClassificationData(fruit.id, context.t('fields.group.name'), fruit.groupe)}
            </Tab>
        );

        tabs.unshift(
            <Tab title={context.t('common.info')} name="info" key={`agriculture-stats-fruit-tab--1`}>
                {getInfoData(data)}
            </Tab>
        );

        return <Tabs>
            {tabs}
        </Tabs>
    }

    return (
        <Page wFull title="pages.overview" state={componentState} actions={seasonDropDown}>
            {getFruitTabs()}
        </Page>
    );
};

export default AgricultureStatsPage;
